import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import {
  Button,
  SmartSearch,
  SelectedFacetTerms,
  CategoriesBreadcrumbs
} from '@components';
import { Icon, Typography, Skeleton, useMediaQuery, breakpoints } from 'gd-design-library';
import { CategoriesBreadcrumb, Category, ProductsResponse } from '@services';

import styles from './ProductListingPageHeader.module.scss';
import { findBaseColor, mapFiltersForSmartSearch } from '@utils';
import { useRouter } from 'next/router';
import { Filters } from './Filters';
import { SmartSearchContext } from '@utils/SmartSearchContext';

interface FilterConfig {
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  showButton?: boolean;
  selectedTerms?: Record<string, string[]>;
  deleteTerm?: (facet: string, term: string) => void;
  deleteColor?: (facet: string, baseColor: string) => void;
  selectTerm?: (facet: string, term: string) => void;
  selectColor?: (facet: string, baseColor: string) => void;
  facetLabelsByName?: Record<string, string | undefined>;
  setPriceRange?: (range: [from: number, to: number] | [from: number, to: number][] | undefined) => void;
  additionalFilters?: React.ReactNode;
}

interface SmartSearchConfig {
  selectedTerms?: Record<string, string[]> | null;
  facets?: ProductsResponse['facets'];
  initialMessage?: string;
  sort?: string;
}

interface SortConfig {
  value?: string;
  onChange?: (value: string) => void;
}

interface CategoryConfig {
  current?: Category | null;
  showTreeFacet?: boolean;
}

interface SectionButtonsConfig {
  sections?: Record<string, ProductsResponse>;
  onSectionClick?: (sectionTitle: string) => void;
}

interface ProductListingPageHeaderProps {
  title: string;
  titlePrefix?: string;
  titleCount?: number;
  isLoading?: boolean;
  productsInfo?: ProductsResponse | null;
  isFacetDataLoading?: boolean;
  filterConfig?: FilterConfig;
  smartSearchConfig?: SmartSearchConfig;
  sortConfig?: SortConfig;
  categoryConfig?: CategoryConfig;
  sectionButtonsConfig?: SectionButtonsConfig;
}

const CN = 'product-listing-page-header';
const breadcrumbsClassName = `${CN}--breadcrumbs`;
const headingClassName = `${CN}--heading`;
const sectionButtonsClassName = `${CN}--section-buttons`;
const headingTitleClassName = `${CN}--heading-title`;

const cx = classNames.bind(styles);

/**
* ProductListingPageHeader component displays the header section of a product listing page
* with title, filtering, sorting, and smart search.
*
* @component
* @param {Object} props - Component props
* @param {string} [props.title] - The title to display at the top of the header
* @param {number} [props.titleCount] - The count to display after the title
* @param {boolean} [props.isLoading=false] - Flag indicating if the component is in loading state
* @param {ProductsResponse | null} [props.productsInfo] - Information about the products being displayed
* @param {boolean} [props.isFacetDataLoading=false] - Flag indicating if facet data is loading
* @param {FilterConfig} [props.filterConfig={}] - Configuration for filtering functionality
* @param {SmartSearchConfig} props.smartSearchConfig - Configuration for smart search functionality
* @param {SortConfig} [props.sortConfig={}] - Configuration for sorting functionality
* @param {CategoryConfig} [props.categoryConfig={}] - Configuration for category navigation
* @param {SectionButtonsConfig} [props.sectionButtonsConfig={}] - Configuration for section buttons functionality
* @returns {React.ReactElement} The rendered ProductListingPageHeader component
*/
export const ProductListingPageHeader: React.FC<ProductListingPageHeaderProps> = ({
  title,
  titlePrefix,
  titleCount,
  isLoading = false,
  productsInfo,
  isFacetDataLoading = false,
  filterConfig = {},
  smartSearchConfig = {},
  sortConfig = {},
  categoryConfig = {},
  sectionButtonsConfig
}) => {
  const [ isMounted, setIsMounted ] = useState(false);
  const isMobileOrTablet = useMediaQuery(`(max-width: ${breakpoints.md})`);
  const router = useRouter();
  const { isSmartSearchOpen } = useContext(SmartSearchContext);
  const isConversationalSearchPage = router.query['conversational-search'] === 'true';

  const getBreadcrumbsCategories = (category: Category | null): CategoriesBreadcrumb[] => {
    const categories: CategoriesBreadcrumb[] = [];
    let currentCategory: Category | null | undefined = category;

    while (currentCategory) {
      categories.push({
        id: currentCategory.id,
        name: currentCategory.name,
        key: currentCategory.key
      });
      currentCategory = currentCategory.parent;
    }

    return categories.reverse().slice(0, -1);
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const displayTitle = titlePrefix && (!isMounted || !isMobileOrTablet) ? `${titlePrefix} ${title}` : title;

  const shouldShowSectionButtons = sectionButtonsConfig?.sections && isMobileOrTablet;
  const shouldShowFilters = !isLoading && !shouldShowSectionButtons;

  return (
    <div className={ cx(CN) }>
      { categoryConfig.current ? (
        <div className={ cx(breadcrumbsClassName) }>
          <CategoriesBreadcrumbs categories={ getBreadcrumbsCategories(categoryConfig.current) }/>
        </div>
      ) : null }
      {isLoading || displayTitle ? (
        <div className={ cx(headingTitleClassName) }>
          {isMounted && !isLoading && isMobileOrTablet && !isConversationalSearchPage ? (
            <Button variant='inherit' isIcon onClick={ (): void => router.back() }>
              <Icon name='arrowLeft' width={ 12 } height={ 12 } />
            </Button>
          ) : null}
          <Typography variant='h3'>
            {!isLoading ? displayTitle : !isMobileOrTablet ? <Skeleton width='300px' /> : null}
            {!isLoading && titleCount ? <span>{` (${titleCount}${(!isMounted || !isMobileOrTablet) ? ' products' : ''})`}</span> : null}
          </Typography>
        </div>
      ) : null}
      <div className={ cx(headingClassName) }>
        <Filters
          isLoading={ isLoading }
          isFacetDataLoading={ isFacetDataLoading }
          isMobileOrTablet={ isMobileOrTablet }
          shouldShowFilters={ shouldShowFilters }
          filterConfig={ filterConfig }
          categoryConfig={ categoryConfig }
          sortConfig={ sortConfig }
          facets={ smartSearchConfig.facets }
          productsInfo={ productsInfo }
        />
        {isMounted ? (
          <SmartSearch
            title='Smart Search'
            initialMessage={ smartSearchConfig.initialMessage }
            filters={ mapFiltersForSmartSearch(filterConfig.selectedTerms) }
            sort={ smartSearchConfig.sort }
          />
        ) : null}
      </div>
      {filterConfig.selectedTerms &&
      (smartSearchConfig.facets || productsInfo?.facets) &&
      filterConfig.deleteTerm && filterConfig.deleteColor && (
        <SelectedFacetTerms
          selectedTerms={ {
            ...(smartSearchConfig.selectedTerms || filterConfig.selectedTerms),
            color: (smartSearchConfig.selectedTerms || filterConfig.selectedTerms)?.color
              ? [ ...new Set(
                (smartSearchConfig.selectedTerms || filterConfig.selectedTerms)?.color
                  .map(color => findBaseColor(color))
              ) ]
              : []
          } }
          facets={ (smartSearchConfig.facets || productsInfo?.facets) }
          deleteTerm={ filterConfig.deleteTerm }
          deleteColor={ filterConfig.deleteColor }
        />
      )}
      {(sectionButtonsConfig?.sections && sectionButtonsConfig.onSectionClick && !isLoading) && (
        <div className={ cx(sectionButtonsClassName, { [`${sectionButtonsClassName}--smart-search-expanded`]: isSmartSearchOpen }) }>
          <span className={ cx(`${sectionButtonsClassName}--star-icon`) } aria-hidden='true' />
          {Object.keys(sectionButtonsConfig.sections).map(sectionTitle => (
            <Button
              variant='outlined'
              key={ sectionTitle }
              onClick={ (): void => sectionButtonsConfig.onSectionClick?.(sectionTitle) }
            >
              {sectionTitle}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductListingPageHeader;
